import { useCallback, useState } from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import {
  AI_LABEL_POSITIONS,
  MAX_AI_LABEL_OPACITY,
  MIN_AI_LABEL_OPACITY,
  loadAiLabelEnabled,
  loadAiLabelOpacity,
  loadAiLabelPosition,
  saveAiLabelEnabled,
  saveAiLabelOpacity,
  saveAiLabelPosition,
  type AiLabelPosition,
} from '@/lib/aiLabelPrefs';

export interface AiLabelPrefs {
  enabled: boolean;
  position: AiLabelPosition;
  /** 0.3–1.0 */
  opacity: number;
  setEnabled: (enabled: boolean) => void;
  setPosition: (position: AiLabelPosition) => void;
  setOpacity: (opacity: number) => void;
}

/**
 * Gemeinsamer Zustand des KI-Hinweises für alle Export-Oberflächen —
 * schreibt jede Änderung direkt in die localStorage-Voreinstellung.
 */
export const useAiLabelPrefs = (): AiLabelPrefs => {
  const [enabled, setEnabledState] = useState<boolean>(loadAiLabelEnabled);
  const [position, setPositionState] = useState<AiLabelPosition>(loadAiLabelPosition);
  const [opacity, setOpacityState] = useState<number>(loadAiLabelOpacity);

  const setEnabled = useCallback((next: boolean) => {
    setEnabledState(next);
    saveAiLabelEnabled(next);
  }, []);

  const setPosition = useCallback((next: AiLabelPosition) => {
    setPositionState(next);
    saveAiLabelPosition(next);
  }, []);

  const setOpacity = useCallback((next: number) => {
    setOpacityState(next);
    saveAiLabelOpacity(next);
  }, []);

  return { enabled, position, opacity, setEnabled, setPosition, setOpacity };
};

/**
 * Checkbox + Ecken-Auswahl für den Hinweis "Mit KI erstellt".
 *
 * Platzierung (gilt an allen Export-Stellen gleich): letzter Punkt der
 * "So sieht der Export aus"-Optionen, direkt über dem Download-Knopf. Damit
 * spiegelt die Reihenfolge in der Oberfläche die Zeichen-Reihenfolge im Bild —
 * der Hinweis kommt zuletzt und liegt obenauf.
 *
 * Der Block bringt seine eigene Trennlinie mit, damit er überall als eigener
 * kleiner Abschnitt lesbar ist und die Aufrufer ihn nur noch platzieren müssen.
 * Die Checkbox ist zugleich die Überschrift; Ecke (und Deckkraft) erscheinen
 * erst, wenn der Hinweis aktiv ist.
 */
export const AiLabelOption: React.FC<AiLabelPrefs & { className?: string }> = ({
  enabled,
  position,
  opacity,
  setEnabled,
  setPosition,
  setOpacity,
  className,
}) => (
  <div className={cn('space-y-2 border-t border-border pt-3', className)}>
    {/* Kästchen statt <input type="checkbox">: die native Variante zeichnet das
        Häkchen im Blau des Browsers und fällt aus dem Orange der übrigen
        Export-Optionen. Gleiche Formensprache wie die Auswahl-Punkte darüber —
        nur eckig, weil an/aus statt Auswahl. */}
    <button
      type="button"
      role="checkbox"
      aria-checked={enabled}
      onClick={() => setEnabled(!enabled)}
      className="flex w-full cursor-pointer items-center gap-3 rounded-lg px-2 py-1.5 text-left hover:bg-muted/50"
    >
      <span
        className={cn(
          'flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-[4px] border-2 transition-colors',
          enabled ? 'border-primary bg-primary text-primary-foreground' : 'border-muted-foreground/40'
        )}
      >
        {enabled && <Check className="h-3 w-3" strokeWidth={3} />}
      </span>
      <span className="flex-1 text-sm font-medium">KI-Hinweis einbrennen</span>
    </button>
    {enabled && (
      <div className="space-y-2 pl-9">
        <div className="flex flex-wrap gap-1.5">
          {AI_LABEL_POSITIONS.map((option) => (
            <button
              key={option.position}
              type="button"
              onClick={() => setPosition(option.position)}
              className={cn(
                'rounded-md border px-2 py-1 text-[11px] font-medium transition-colors',
                position === option.position
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border text-muted-foreground hover:border-primary/40'
              )}
            >
              {option.label}
            </button>
          ))}
        </div>
        <div>
          <div className="mb-1.5 flex items-center justify-between">
            <Label className="text-xs text-muted-foreground">Deckkraft</Label>
            <span className="font-mono text-xs">{Math.round(opacity * 100)}%</span>
          </div>
          <Slider
            min={Math.round(MIN_AI_LABEL_OPACITY * 100)}
            max={Math.round(MAX_AI_LABEL_OPACITY * 100)}
            step={5}
            value={[Math.round(opacity * 100)]}
            onValueChange={([value]) => setOpacity(value / 100)}
          />
        </div>
      </div>
    )}
  </div>
);
